/**
 * YouTube URL helpers — pure, no React.
 *
 * Moved out of content/video-player.tsx when the V2 VideoPlayer was
 * superseded by the v3 media components (media-hero-card / related-media-
 * panel); the extractor is shared by those, useYouTubeDuration, the voice
 * pinned card and the thinking timeline.
 */

/**
 * Extract YouTube video ID from various URL formats:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 */
export function getYouTubeId(url: string): string | null {
  try {
    const urlObj = new URL(url);
    if (
      urlObj.hostname === 'www.youtube.com' ||
      urlObj.hostname === 'youtube.com'
    ) {
      // /watch?v=ID format
      const videoId = urlObj.searchParams.get('v');
      if (videoId) return videoId;
      // /embed/ID format
      const embedMatch = urlObj.pathname.match(/\/embed\/([^/?]+)/);
      if (embedMatch) return embedMatch[1];
      // /shorts/ID format — Gurudev Shorts are regular YouTube videos, so
      // extracting the id here makes them render as playable Video (not Article).
      const shortsMatch = urlObj.pathname.match(/\/shorts\/([^/?]+)/);
      if (shortsMatch) return shortsMatch[1];
    }
    if (urlObj.hostname === 'youtu.be') {
      return urlObj.pathname.slice(1) || null;
    }
  } catch {
    // Invalid URL
  }
  return null;
}

/** Static poster/thumbnail URL for a YouTube video id. */
export function youTubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

/** Embed URL for the inline iframe player. */
export function youTubeEmbedUrl(
  videoId: string,
  opts: { autoplay?: boolean } = {}
): string {
  return `https://www.youtube.com/embed/${videoId}?rel=0${opts.autoplay ? '&autoplay=1' : ''}`;
}
